/**
 * Returns portal host map: the frontend mirror of
 * `backend/config/returnsHosts.js`.
 *
 * Firebase Hosting serves one build on every custom domain, so the only way
 * the portal can know which store a customer is looking at is the hostname it
 * was opened on. Host wins over `?store=`, because a customer who lands on
 * returns.centralcoastcoffee.com.au should never be able to file a Phaser FPV
 * return by pasting a query string.
 *
 * Keep this table identical to the backend one: the API resolves the store the
 * same way and will reject a mismatch.
 */

export const RETURNS_HOSTS = {
  'returns.phaserfpv.com.au': 'phaser-fpv',
  'returns.tradiemods.au': 'tradie-mods',
  'returns.centralcoastcoffee.com.au': 'central-coast-coffee',
  'returns.porlex.com.au': 'porlex-australia',
  'returns.redeemer.coffee': 'ace176-c7'
};

/** Every host we serve the portal on, in display order. */
export const RETURNS_HOST_LIST = Object.keys(RETURNS_HOSTS).map((hostname) => ({
  hostname,
  storeKey: RETURNS_HOSTS[hostname]
}));

function normaliseHost(hostname) {
  return String(hostname || '')
    .trim()
    .toLowerCase()
    .replace(/\.$/, '')
    .replace(/:\d+$/, '');
}

/** The store a hostname belongs to, or null when it is not a returns host. */
export function storeKeyForHost(hostname) {
  return RETURNS_HOSTS[normaliseHost(hostname)] || null;
}

/** True when the portal owns this hostname (so `/` renders it too). */
export function isReturnsHost(hostname) {
  return storeKeyForHost(hostname) !== null;
}

/**
 * Resolve the store for a page load: hostname first, then `?store=`.
 * Returns null when neither names a store we know, which makes the portal
 * show its store chooser.
 */
export function resolveReturnsStore({ hostname, search } = {}) {
  const fromHost = storeKeyForHost(hostname);
  if (fromHost) return fromHost;

  const raw = String(search || '');
  const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
  const requested = String(params.get('store') || '').trim().toLowerCase();
  if (!requested) return null;

  const known = Object.values(RETURNS_HOSTS).includes(requested);
  return known ? requested : null;
}

export default RETURNS_HOSTS;
